// GeoForcedModal — Phase R4 Step 3.
//
// ════════════════════════════════════════════════════════════════════
// PURPOSE
// ════════════════════════════════════════════════════════════════════
//
// Hard-block on the agent shell that fires the FIRST time a logged-in
// agent has no precise GPS coordinates on file:
//
//   `admin.role === 'agent' && (admin.latitude == null || admin.longitude == null)`
//
// The agent CANNOT see, click, or interact with anything else in the
// app until they capture their GPS one-tap. This is the mechanism
// that backfills the geo column for legacy agents AND for agents
// whose creator only supplied rough area data — without it the
// Hyper-Local Mesh Network can never escape the L5 nation tie-break.
//
// ════════════════════════════════════════════════════════════════════
// HOW THE NON-DISMISSIBILITY IS ENFORCED
// ════════════════════════════════════════════════════════════════════
//
// Every common dismissal vector is explicitly blocked:
//
//   1. NO close button rendered.
//   2. NO X / cancel CTA. The only button is "Use my current location".
//   3. The backdrop has NO `onClick={onClose}` handler — clicking
//      outside the modal does nothing.
//   4. ESC key suppression: a `keydown` listener on `window` calls
//      `e.stopPropagation()` + `e.preventDefault()` for the Escape
//      key while the modal is mounted.
//   5. `body { overflow: hidden }` on mount → the page underneath
//      cannot scroll, removing the visual bypass of "scroll past
//      the modal".
//   6. Z-index 9999 (well above every other overlay including
//      Toasts) so nothing else can render on top + steal focus.
//   7. The whole shell is rendered AROUND the modal but is
//      `pointer-events: none` underneath via the backdrop, so even
//      a determined user dev-tools-removing the modal would still
//      have to disable the backdrop too.
//
// The ONE legitimate exit: a successful PATCH /admin/me-agent/location
// with at least { latitude, longitude }. On success we call
// `refresh()` (from useAdminAuth) which re-hydrates the auth state
// from /admin/auth/me; the gating expression in App.tsx now
// evaluates false; the modal unmounts.
//
// If the user denies browser geolocation permission, the LocationCapture
// component surfaces that error inside the modal and offers the
// pincode/manual fallback. Pincode-only completion is REJECTED by
// the modal (no lat/lng → modal stays up) — the locked rule is
// "agent must have precise GPS" so a pincode-only sub-area pick is
// not enough to dismiss. The wording is explicit about this.

import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import LocationCapture, {
  type GeoBag,
  EMPTY_GEO_BAG,
  isGeoLocated,
} from '@baaligames/shared-ui/components/LocationCapture';
import Icon from '@baaligames/shared-ui/components/Icon';
import { INPUT_CLS } from './fieldStyles';

export default function GeoForcedModal() {
  const { refresh } = useAdminAuth();
  const { push } = useToast();
  const t = useT();
  const [geo, setGeo] = useState<GeoBag>(EMPTY_GEO_BAG);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ── ESC key suppression (non-dismissibility guard #4) ──
  // Captures any escape keypress at the document level and
  // swallows it so React-router / shell-shortcut handlers can't
  // route the user away from the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, []);

  // ── Body scroll lock (non-dismissibility guard #5) ──
  // Restores the prior overflow on unmount so once the modal is
  // dismissed legitimately the rest of the app scrolls again.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Submit the GPS to the server ──
  // Only fires when the geo bag has BOTH lat and lng — this is the
  // explicit locked rule "agent must have precise GPS to receive
  // players from the locator". A pincode-only or manual-only fill
  // is INSUFFICIENT to dismiss the modal (we surface that to the
  // user via the disabled state on the submit button).
  const submit = useCallback(async () => {
    if (!isGeoLocated(geo)) return;  // L-6: GPS preferred, not required
    setBusy(true); setErr(null);
    try {
      // Forward the FULL bag the user picked — lat/lng is the
      // mandatory minimum, but if they also picked area/district/
      // state via the cascade we send those too so the agent's
      // row gets the richest possible attribution data in one call.
      const body: Record<string, unknown> = {};
      if (geo.latitude  != null) body.latitude  = geo.latitude;
      if (geo.longitude != null) body.longitude = geo.longitude;
      if (geo.state_id    != null) body.state_id    = geo.state_id;
      if (geo.district_id != null) body.district_id = geo.district_id;
      if (geo.area_id     != null) body.area_id     = geo.area_id;
      // Phase R6 (2026-05-13): pincode_id now persisted on agents
      // (migration 069). Forward it so the broadened gate condition
      // (pincode_id != null required) is actually satisfied after
      // the modal submits — otherwise the agent would lock GPS,
      // close the modal, and the next pageview would re-open it
      // because pincode_id was never sent.
      if (geo.pincode_id  != null) body.pincode_id  = geo.pincode_id;
      if (geo.nation)              body.nation      = geo.nation;
      await adminApi.patch('/admin/me-agent/location', body);
      // Success — re-hydrate auth so the App.tsx gate re-evaluates
      // `admin.latitude == null` and unmounts this modal.
      await refresh();
      push('success', t('geo.toast.success.agent'));
      // No explicit unmount call: the parent component conditionally
      // renders us based on `admin.latitude == null`, so the refresh
      // above causes our parent to drop us automatically.
    } catch (e) {
      setErr((e as ApiError).message || t('agent.geo.error.fallback'));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, refresh, push]);

  const canSubmit = isGeoLocated(geo) && !busy;

  return (
    <div
      // Backdrop — NO onClick handler (non-dismissibility guard #3).
      // pointer-events: auto here means the backdrop catches every
      // click + does nothing with it, blocking the shell underneath.
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm
                 flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="geo-forced-modal-title"
      aria-describedby="geo-forced-modal-body"
    >
      <div
        className="w-full max-w-lg bg-white dark:bg-arena-900
                   rounded-2xl border border-arena-200/60 dark:border-arena-600/50
                   shadow-2xl my-auto"
      >
        {/* Header — emphatic, no close button. */}
        <div className="px-5 py-4 border-b border-arena-200/40 dark:border-arena-700/60
                        bg-gradient-to-br from-fuchsia-50 to-violet-50
                        dark:from-fuchsia-500/10 dark:to-violet-500/10
                        rounded-t-2xl">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-xl
                             bg-fuchsia-100 text-fuchsia-700
                             dark:bg-fuchsia-400/20 dark:text-fuchsia-300 shrink-0">
              <Icon name="my_location" size={22} filled />
            </span>
            <div className="min-w-0">
              <h2
                id="geo-forced-modal-title"
                className="text-lg font-extrabold text-arena-900 dark:text-white"
              >
                {t('agent.geo.title')}
              </h2>
              <p className="text-[12.5px] text-arena-700 dark:text-neon-200/80 mt-0.5 leading-relaxed">
                {t('agent.geo.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Body — explanation + LocationCapture. */}
        <div id="geo-forced-modal-body" className="px-5 py-4 space-y-4">
          <div className="text-[13px] text-arena-700 dark:text-neon-100/85 leading-relaxed space-y-2">
            <p>{t('agent.geo.body.primary')}</p>
            <p className="text-[12px] text-arena-600 dark:text-neon-200/80">
              {t('agent.geo.body.secondary')}
            </p>
          </div>

          {/* LocationCapture in NORMAL (GPS-enabled) mode — this is
              the agent capturing their OWN coordinates, which is
              exactly what we want. The forced flow accepts ONLY
              GPS-derived coords as sufficient (canSubmit gate
              below requires both lat AND lng), so the user
              physically cannot click their way out by picking a
              pincode. The pincode/manual fields stay visible so a
              user with denied GPS permission can at least understand
              why they're stuck and contact admin to re-enable. */}
          <LocationCapture
            value={geo}
            onChange={setGeo}
            mode="self"                          // agent capturing OWN coords; pin → GPS → distance check
            showSavedSummary={false}             // onboarding has no pre-existing value; go straight to editor
            disabled={busy}
            inputClassName={INPUT_CLS}
          />

          {err && (
            <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10
                            border border-rose-200 dark:border-rose-400/30
                            text-rose-700 dark:text-rose-200 text-xs px-3 py-2">
              {err}
            </div>
          )}

          {/* Help text mirrors the canSubmit gate so the user knows
              why the button is disabled when they've only filled in
              area/pincode. */}
          {!canSubmit && !busy && (
            <p className="text-[11.5px] text-amber-700 dark:text-amber-300">
              {t('agent.geo.hint.disabled')}
            </p>
          )}
        </div>

        {/* Footer — single CTA, no cancel/dismiss. */}
        <div className="px-5 py-4 border-t border-arena-200/40 dark:border-arena-700/60
                        rounded-b-2xl">
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="w-full px-4 py-3 rounded-xl text-sm font-extrabold uppercase
                       tracking-wider bg-fuchsia-600 hover:bg-fuchsia-700
                       active:bg-fuchsia-800 text-white
                       shadow-[0_2px_12px_rgba(192,38,211,0.45)]
                       inline-flex items-center justify-center gap-2
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition"
          >
            {busy ? (
              <>
                <Icon name="progress_activity" size={16} className="animate-spin" />
                {t('agent.geo.cta.saving')}
              </>
            ) : (
              <>
                <Icon name="check_circle" size={16} />
                {t('agent.geo.cta.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
